import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { AppWorkerClient } from '../../../../data/AppWorkerClient'
import type { MetricComparisonResponse, ServerApiClient } from '../../../../data/ServerApiClient'
import { CFO_DRILLDOWN_MANIFEST } from '../../../../ai/cfoDrilldownManifest'
import { isOpenEndedAskEnabled } from '../../../../ai/openEndedAsk'
import { CfoAnalysisModal } from './CfoAnalysisModal'
import { CfoMonthlyReportPanel } from './CfoMonthlyReportPanel'
import { buildCfoInstantCardFromContext } from './instantCards'
import type {
  CfoAnalysisContext,
  CfoAnalysisGrounding,
  CfoInstantCard,
  CfoInstantCardEnrichment,
  CfoAnalysisMode,
  CfoAnalysisVisualSpec,
  CfoPersistedSession,
  CfoReportAnalysisItem,
} from './types'
import { mapInstantCardEnrichmentPacket } from './CfoAnalysisModal'

// CfoAnalysisProvider — the single AI workspace host for CFO Core. Mounted once
// around the unified surface; every page registers its live context, and any
// chart/value/Ask affordance opens ONE shared analysis modal seeded with that
// evidence. Replaces the per-page copilot rail.

/** Identifiers a "view transactions" action carries into a page's inline GL opener. */
export type CfoAnalysisGlDetailTarget = {
  metricId?: string | null
  accountId?: string | null
  metricLabel?: string | null
  /** Clicked cell's period (YYYY-MM-DD) so the inline GL view scopes to that
   *  month instead of the page's latest/selected end period. */
  period?: string | null
}

/** Live context a page publishes so the modal can ground the agent. */
export type CfoAnalysisPageContext = {
  pageId: string
  activeSection: string
  duckDbOpts?: Record<string, any> | null
  getComparisons?: () => MetricComparisonResponse | null
  /** Canonical source tables/views backing the current page evidence. */
  sourceTables?: string[] | null
  /** Page-level evidence scope to pass through workspace_context.selection. */
  selection?: Record<string, any> | null
  /** Selected end period the page is showing (YYYY-MM-DD). */
  period?: string | null
  /** Comparison mode id/label the page is showing. */
  comparison?: string | null
  /** Human "as of" label, e.g. "June 2026". */
  asOfLabel?: string | null
  /** Default page-aware suggested prompts for Ask mode. */
  suggestedPrompts?: string[]
  /** Prebuilt deterministic cards for the current page/materialized artifact. */
  instantCards?: CfoInstantCard[] | null
  /** Fingerprint for the current page facts/artifact set. */
  pageFactsFingerprint?: string | null
  /** Page-specific deterministic card resolver for clicked evidence. */
  resolveInstantCard?: (input: { context: CfoAnalysisContext; visual?: CfoAnalysisVisualSpec | null; instantCards?: CfoInstantCard[] | null }) => CfoInstantCard | null
  /** Inline GL-detail opener: return true when the page opened a scoped GL view
   *  in place (e.g. the Financials bounded GL modal), so the analysis modal
   *  skips the full-page navigation to the GL detail explorer. */
  openGlDetail?: (target: CfoAnalysisGlDetailTarget) => boolean
}

export type OpenAnalysisOptions = {
  /** Seeded evidence card rendered before the AI narrates. */
  visual?: CfoAnalysisVisualSpec | null
  /** First prompt sent on open. Auto-generated from metric label if omitted. */
  seedPrompt?: string | null
  metricId?: string | null
  metricLabel?: string | null
  comparison?: string | null
  sourceComponent?: string | null
  sourceTables?: string[] | null
  selection?: Record<string, any> | null
  /** Authoritative client-side facts forwarded to the agent for instant grounding. */
  grounding?: CfoAnalysisGrounding | null
  /** Deterministic card to render instantly and forward compactly to the agent. */
  instantCard?: CfoInstantCard | null
  /** Short label for the history list. */
  label?: string | null
}

/** Snapshot taken at open time so the agent config stays stable for the session. */
export type CfoAnalysisActiveSession = {
  id: string
  context: CfoAnalysisContext
  mode: CfoAnalysisMode
  seedPrompt?: string | null
  label: string
  openedAt: number
  visual: CfoAnalysisVisualSpec | null
  instantCard: CfoInstantCard | null
  page: CfoAnalysisPageContext
  monthKey: string
  /** When set, the modal renders this saved transcript read-only (History). */
  restored?: {
    transcript: CfoPersistedSession['transcript']
    answer: CfoPersistedSession['answer']
    visuals: CfoPersistedSession['visuals']
    manifestVersion?: string | null
    recipeId?: string | null
  } | null
}

type CfoAnalysisContextValue = {
  openAnalysis: (opts: OpenAnalysisOptions) => void
  openAsk: () => void
  openReport: (period?: string | null) => void
  tagToReport: (item: Partial<CfoReportAnalysisItem>) => Promise<void>
  close: () => void
  registerPageContext: (ctx: CfoAnalysisPageContext) => void
}

/** Stable, server-usable session id (UUID), with a non-crypto fallback. */
let analysisSessionFallback = 0
function newSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* ignore */
  }
  analysisSessionFallback += 1
  return `cfo-analysis-${Date.now().toString(36)}-${analysisSessionFallback}`
}

/** Normalize the server's session list payload into typed records. */
function mapPersistedSessions(resp: any, wb: string): CfoPersistedSession[] {
  const sessions: CfoPersistedSession[] = Array.isArray(resp?.sessions)
    ? resp.sessions.map((s: any) => ({
        id: String(s?.id || ''),
        label: String(s?.label || '') || 'Analysis',
        mode: (s?.mode as CfoAnalysisMode) || 'ask',
        manifest_version: s?.manifest_version ?? null,
        recipe_id: s?.recipe_id ?? null,
        period: String(s?.period || ''),
        context:
          (s?.context as CfoAnalysisContext) ||
          ({ workbookId: wb, pageId: 'home', activeSection: 'cfo_home' } as CfoAnalysisContext),
        visual: (s?.visual as CfoAnalysisVisualSpec) ?? null,
        instantCard: ((s?.instant_card ?? s?.instantCard) as CfoInstantCard) ?? null,
        transcript: Array.isArray(s?.transcript) ? s.transcript : [],
        answer: s?.answer ?? null,
        visuals: Array.isArray(s?.visuals) ? s.visuals : null,
        archived: !!s?.archived,
        updatedAt: s?.updated_at ?? null,
      }))
    : []
  return sessions.filter((s) => s.id)
}

const Ctx = createContext<CfoAnalysisContextValue | null>(null)

/** Access the analysis workspace from any CFO page/component. */
export function useCfoAnalysis(): CfoAnalysisContextValue {
  const value = useContext(Ctx)
  if (!value) {
    throw new Error('useCfoAnalysis must be used within a CfoAnalysisProvider')
  }
  return value
}

/** Like useCfoAnalysis, but returns null outside a provider — for shared
 *  hooks/components that should degrade gracefully in standalone hosts. */
export function useCfoAnalysisOptional(): CfoAnalysisContextValue | null {
  return useContext(Ctx)
}

/**
 * Publish the active page's context so the modal can ground the agent and seed
 * evidence. Call once per page with a memoized context object.
 */
export function useRegisterCfoAnalysisPage(ctx: CfoAnalysisPageContext): void {
  const { registerPageContext } = useCfoAnalysis()
  const {
    pageId,
    activeSection,
    duckDbOpts,
    getComparisons,
    sourceTables,
    selection,
    period,
    comparison,
    asOfLabel,
    suggestedPrompts,
    instantCards,
    pageFactsFingerprint,
    resolveInstantCard,
    openGlDetail,
  } = ctx
  useEffect(() => {
    registerPageContext({
      pageId,
      activeSection,
      duckDbOpts,
      getComparisons,
      sourceTables,
      selection,
      period,
      comparison,
      asOfLabel,
      suggestedPrompts,
      instantCards,
      pageFactsFingerprint,
      resolveInstantCard,
      openGlDetail,
    })
  }, [
    registerPageContext,
    pageId,
    activeSection,
    duckDbOpts,
    getComparisons,
    sourceTables,
    selection,
    period,
    comparison,
    asOfLabel,
    suggestedPrompts,
    instantCards,
    pageFactsFingerprint,
    resolveInstantCard,
    openGlDetail,
  ])
}

/** Derive a `YYYY-MM` report key from a period (YYYY-MM-DD) or as-of date. */
export function toMonthKey(period?: string | null): string {
  const text = String(period ?? '').trim()
  const match = text.match(/(\d{4})-(\d{2})/)
  if (match) return `${match[1]}-${match[2]}`
  const parsed = new Date(text)
  if (!Number.isNaN(parsed.getTime())) {
    return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}`
  }
  const now = new Date()
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
}

function instantCardRichnessScore(card?: CfoInstantCard | null): number {
  if (!card) return 0
  let score = 1
  score += Math.min(12, card.trend?.points?.length || 0)
  score += Math.min(4, card.comparisons?.length || 0) * 3
  score += Math.min(3, card.insights?.length || 0) * 2
  score += Math.min(3, card.prescriptiveActions?.length || 0) * 2
  score += Math.min(5, card.nextSteps?.length || 0)
  score += Math.min(5, card.actions?.length || 0)
  score += Math.min(6, card.supportingFacts?.length || 0)
  return score
}

export function shouldHydratePersistedInstantCard(
  current: CfoInstantCard | null | undefined,
  persisted: CfoInstantCard | null | undefined,
): boolean {
  if (!persisted?.cardKey) return false
  if (!current?.cardKey) return true
  if (persisted.cardKey !== current.cardKey) return false
  const currentFingerprint = String(current.freshness?.sourceFingerprint || '').trim()
  const persistedFingerprint = String(persisted.freshness?.sourceFingerprint || '').trim()
  if (currentFingerprint && persistedFingerprint && currentFingerprint !== persistedFingerprint) return false
  if (current.version && persisted.version && current.version !== persisted.version) return false
  return instantCardRichnessScore(persisted) >= instantCardRichnessScore(current)
}

const enrichmentCardFreshness = (card?: CfoInstantCard | null): string =>
  String(card?.freshness?.sourceFingerprint || '').trim()

export function instantCardEnrichmentCacheKey(card?: CfoInstantCard | null): string {
  if (!card?.cardKey) return ''
  return [
    String(card.surface || '').trim(),
    String(card.cardKey || '').trim(),
    String(card.version || '').trim(),
    enrichmentCardFreshness(card),
  ].join('|')
}

export function selectCachedInstantCardEnrichment(
  card: CfoInstantCard | null | undefined,
  packets: Record<string, CfoInstantCardEnrichment>,
): CfoInstantCardEnrichment | null {
  const key = instantCardEnrichmentCacheKey(card)
  const packet = key ? packets[key] : null
  if (!packet || packet.status !== 'ready') return null
  const expectedFreshness = enrichmentCardFreshness(card)
  const packetFreshness = String(packet.freshnessToken || '').trim()
  if (expectedFreshness && packetFreshness && expectedFreshness !== packetFreshness) return null
  if (packet.cardKey !== card?.cardKey) return null
  return packet
}

export type CfoAnalysisProviderProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  children: ReactNode
}

export function CfoAnalysisProvider({ appApi, workerClient = null, workbookId, children }: CfoAnalysisProviderProps) {
  const pageRef = useRef<CfoAnalysisPageContext>({ pageId: 'home', activeSection: 'cfo_home' })
  const persistedCardsSigRef = useRef('')
  const [session, setSession] = useState<CfoAnalysisActiveSession | null>(null)
  const [enrichmentPackets, setEnrichmentPackets] = useState<Record<string, CfoInstantCardEnrichment>>({})
  const [history, setHistory] = useState<CfoPersistedSession[]>([])
  const [historyQuery, setHistoryQuery] = useState('')
  const [historyArchived, setHistoryArchived] = useState(false)
  const [reportPeriod, setReportPeriod] = useState<string | null>(null)
  const [accountingProvider, setAccountingProvider] = useState<string | null>(null)

  useEffect(() => {
    const wb = String(workbookId || '').trim()
    let cancelled = false
    setAccountingProvider(null)
    if (!wb) return () => { cancelled = true }
    const getBootstrap = (appApi as Partial<ServerApiClient>).getAccountingWorkbookBootstrap
    if (typeof getBootstrap !== 'function') {
      setAccountingProvider('quickbooks')
      return () => { cancelled = true }
    }
    void getBootstrap.call(appApi, wb)
      .then((value) => { if (!cancelled) setAccountingProvider(value.accounting_provider || null) })
      .catch(() => { if (!cancelled) setAccountingProvider(null) })
    return () => { cancelled = true }
  }, [appApi, workbookId])

  const rememberEnrichmentPacket = useCallback((packet: CfoInstantCardEnrichment | null | undefined, card?: CfoInstantCard | null) => {
    if (!packet || packet.status !== 'ready' || !packet.cardKey) return
    const key = instantCardEnrichmentCacheKey(card || { cardKey: packet.cardKey, surface: '', version: '', freshness: { sourceFingerprint: packet.freshnessToken || null } } as CfoInstantCard)
    if (!key) return
    setEnrichmentPackets((prev) => {
      const current = prev[key]
      if (current?.payloadHash && packet.payloadHash && current.payloadHash === packet.payloadHash) return prev
      return { ...prev, [key]: packet }
    })
  }, [])

  const persistInstantCards = useCallback((cards: CfoInstantCard[] | null | undefined, fingerprint?: string | null) => {
    const wb = String(workbookId || '').trim()
    const unique = Array.from(new Map((cards || []).filter((card) => card?.cardKey).map((card) => [card.cardKey, card])).values())
    if (!wb || !unique.length || accountingProvider !== 'quickbooks') return
    const sig = `${fingerprint || ''}:${unique.map((card) => `${card.cardKey}:${card.freshness?.sourceFingerprint || ''}`).join('|')}`
    if (persistedCardsSigRef.current === sig) return
    persistedCardsSigRef.current = sig
    if (typeof (appApi as any)?.saveQuickBooksCfoInstantCards === 'function') {
      void (appApi as any)
        .saveQuickBooksCfoInstantCards(wb, { cards: unique })
        .then((resp: any) => {
          const packets = Array.isArray(resp?.packets) ? resp.packets : []
          for (const packetRow of packets) {
            const packet = mapInstantCardEnrichmentPacket(packetRow, true)
            const card = unique.find((candidate) => candidate.cardKey === packet?.cardKey)
            rememberEnrichmentPacket(packet, card)
          }
        })
        .catch(() => {})
    } else {
      for (const card of unique) {
        if (typeof appApi?.saveQuickBooksCfoInstantCard === 'function') {
          void appApi
            .saveQuickBooksCfoInstantCard(wb, { card })
            .then((resp: any) => {
              const packet = mapInstantCardEnrichmentPacket(Array.isArray(resp?.packets) ? resp.packets[0] : null, true)
              rememberEnrichmentPacket(packet, card)
            })
            .catch(() => {})
        }
      }
    }
    if (typeof (appApi as any)?.ensureQuickBooksCfoInstantCardEnrichment === 'function') {
      for (const card of unique.slice(0, 30)) {
        void (appApi as any)
          .ensureQuickBooksCfoInstantCardEnrichment(wb, { card })
          .then((resp: any) => {
            const packet = mapInstantCardEnrichmentPacket(resp?.packet || resp, Boolean(resp?.cache_hit))
            rememberEnrichmentPacket(packet, card)
          })
          .catch(() => {})
      }
    }
    if (typeof (appApi as any)?.ensureQuickBooksCfoInstantCardContext === 'function') {
      for (const card of unique.slice(0, 30)) {
        void (appApi as any)
          .ensureQuickBooksCfoInstantCardContext(wb, { card })
          .catch(() => {})
      }
    }
  }, [accountingProvider, appApi, rememberEnrichmentPacket, workbookId])

  const registerPageContext = useCallback((ctx: CfoAnalysisPageContext) => {
    pageRef.current = ctx
    persistInstantCards(ctx.instantCards, ctx.pageFactsFingerprint)
  }, [persistInstantCards])

  // Load persisted sessions for the workbook (filtered by search + archived
  // state) so History survives refresh, is searchable, and supports archive.
  // Debounced while typing a search query.
  useEffect(() => {
    const wb = String(workbookId || '').trim()
    if (!session || !wb || typeof appApi?.listQuickBooksCfoAnalysisSessions !== 'function') {
      setHistory([])
      return
    }
    let cancelled = false
    const q = historyQuery.trim()
    const handle = setTimeout(
      () => {
        appApi
          .listQuickBooksCfoAnalysisSessions(wb, {
            limit: 50,
            q: q || null,
            archived: historyArchived ? 'true' : 'false',
          })
          .then((resp: any) => {
            if (!cancelled) setHistory(mapPersistedSessions(resp, wb))
          })
          .catch(() => {
            if (!cancelled) setHistory([])
          })
      },
      q ? 250 : 0,
    )
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [appApi, workbookId, historyQuery, historyArchived, session])

  const buildContext = useCallback(
    (opts: OpenAnalysisOptions): CfoAnalysisContext => {
      const page = pageRef.current
      return {
        workbookId,
        pageId: page.pageId,
        activeSection: page.activeSection,
        period: page.period ?? null,
        comparison: opts.comparison ?? page.comparison ?? null,
        asOfLabel: page.asOfLabel ?? null,
        metricId: opts.metricId ?? null,
        metricLabel: opts.metricLabel ?? null,
        sourceComponent: opts.sourceComponent ?? null,
        sourceTables: opts.sourceTables ?? page.sourceTables ?? null,
        selection: opts.selection ?? page.selection ?? null,
        grounding: opts.grounding ?? null,
        instantCard: opts.instantCard ?? null,
      }
    },
    [workbookId],
  )

  const startSession = useCallback(
    (mode: CfoAnalysisMode, context: CfoAnalysisContext, opts: OpenAnalysisOptions) => {
      const page = pageRef.current
      const monthKey = toMonthKey(context.period || context.asOfLabel)
      const instantCard =
        opts.instantCard ??
        context.instantCard ??
        page.resolveInstantCard?.({ context, visual: opts.visual ?? null, instantCards: page.instantCards ?? null }) ??
        (mode === 'evidence' ? buildCfoInstantCardFromContext({ context, visual: opts.visual ?? null }) : null)
      const label =
        opts.label?.trim() ||
        (mode === 'ask'
          ? `Ask · ${context.asOfLabel || monthKey}`
          : `${context.metricLabel || context.sourceComponent || 'Evidence'} · ${context.asOfLabel || monthKey}`)
      const next: CfoAnalysisActiveSession = {
        id: newSessionId(),
        context: { ...context, instantCard },
        mode,
        seedPrompt: opts.seedPrompt ?? null,
        label,
        openedAt: Date.now(),
        visual: opts.visual ?? null,
        instantCard,
        page,
        monthKey,
        restored: null,
      }
      // Empty sessions are not added to History; the modal persists a session
      // (and it appears in History) only once it has produced an answer.
      setSession(next)
      return next
    },
    [],
  )

  const hydratePersistedInstantCard = useCallback(
    (active: CfoAnalysisActiveSession, explicitCard: boolean) => {
      if (explicitCard) return
      const wb = String(workbookId || '').trim()
      const cardKey = active.instantCard?.cardKey
      if (!wb || !cardKey || typeof appApi?.listQuickBooksCfoInstantCards !== 'function') return
      void appApi
        .listQuickBooksCfoInstantCards(wb, {
          cardKey,
          surface: active.instantCard?.surface || active.context.activeSection || null,
          limit: 1,
        })
        .then((resp: any) => {
          const row = Array.isArray(resp?.cards) ? resp.cards[0] : null
          const card = (row?.payload_json ?? row?.payload ?? row?.card ?? row) as CfoInstantCard | null
          if (!card || !card.cardKey) return
          setSession((cur) => {
            if (!cur || cur.id !== active.id) return cur
            if (!shouldHydratePersistedInstantCard(cur.instantCard, card)) return cur
            return {
              ...cur,
              instantCard: card,
              context: { ...cur.context, instantCard: card },
            }
          })
        })
        .catch(() => {
          /* derived card is already rendered; persisted lookup is best-effort */
        })
    },
    [appApi, workbookId],
  )

  // Save/refresh a session's full transcript + answer to the workbook, and
  // mirror it into the in-memory History list (newest first, deduped by id).
  const persistSession = useCallback(
    (record: CfoPersistedSession) => {
      // Optimistically mirror into the active (non-archived) History view.
      if (!historyArchived) {
        setHistory((prev) => [record, ...prev.filter((s) => s.id !== record.id)].slice(0, 60))
      }
      const wb = String(workbookId || '').trim()
      if (!wb || !record.id || typeof appApi?.saveQuickBooksCfoAnalysisSession !== 'function') return
      const instantCard = record.instantCard ?? record.context?.instantCard ?? null
      if (instantCard && typeof appApi?.saveQuickBooksCfoInstantCard === 'function') {
        void appApi.saveQuickBooksCfoInstantCard(wb, { card: instantCard }).catch(() => {})
      }
      void appApi
        .saveQuickBooksCfoAnalysisSession(wb, record.id, {
          session: {
            period: record.period,
            label: record.label,
            mode: record.mode,
            manifest_version: record.manifest_version ?? CFO_DRILLDOWN_MANIFEST.version,
            recipe_id: record.recipe_id ?? null,
            context: record.context,
            visual: record.visual ?? null,
            instant_card: instantCard,
            transcript: record.transcript,
            answer: record.answer ?? null,
            visuals: record.visuals ?? null,
          },
        })
        .catch(() => {
          /* best-effort; History still works in-memory this session */
        })
    },
    [appApi, workbookId, historyArchived],
  )

  // Permanently delete a saved session (and close it if it's open).
  const deleteSession = useCallback(
    (id: string) => {
      setHistory((prev) => prev.filter((s) => s.id !== id))
      setSession((cur) => (cur?.id === id ? null : cur))
      const wb = String(workbookId || '').trim()
      if (!wb || !id || typeof appApi?.deleteQuickBooksCfoAnalysisSession !== 'function') return
      void appApi.deleteQuickBooksCfoAnalysisSession(wb, id).catch(() => {})
    },
    [appApi, workbookId],
  )

  // Archive / unarchive a saved session. It leaves the current (active or
  // archived) view immediately since each view shows one bucket.
  const archiveSession = useCallback(
    (id: string, archived: boolean) => {
      setHistory((prev) => prev.filter((s) => s.id !== id))
      const wb = String(workbookId || '').trim()
      if (!wb || !id || typeof appApi?.patchQuickBooksCfoAnalysisSession !== 'function') return
      void appApi.patchQuickBooksCfoAnalysisSession(wb, id, { archived }).catch(() => {})
    },
    [appApi, workbookId],
  )

  // Open a saved session read-only (History click) — restores the transcript.
  const reopenSession = useCallback((record: CfoPersistedSession) => {
    const instantCard = record.instantCard ?? record.context?.instantCard ?? null
    setSession({
      id: record.id,
      context: { ...record.context, instantCard },
      mode: record.mode,
      seedPrompt: null,
      label: record.label,
      openedAt: Date.now(),
      visual: record.visual ?? null,
      instantCard,
      page: pageRef.current,
      monthKey: record.period || toMonthKey(record.context?.period),
      restored: {
        transcript: record.transcript || [],
        answer: record.answer ?? null,
        visuals: record.visuals ?? null,
        manifestVersion: record.manifest_version ?? null,
        recipeId: record.recipe_id ?? null,
      },
    })
  }, [])

  // From a restored view, resume a live conversation with the same context.
  const continueSession = useCallback(
    (active: CfoAnalysisActiveSession, seedPrompt?: string | null) => {
      startSession(active.mode === 'history' ? 'ask' : active.mode, active.context, {
        visual: active.visual,
        instantCard: active.instantCard,
        seedPrompt: seedPrompt ?? null,
        label: active.label,
      })
    },
    [startSession],
  )

  const openAnalysis = useCallback(
    (opts: OpenAnalysisOptions) => {
      const context = buildContext(opts)
      const seedPrompt =
        opts.seedPrompt?.trim() ||
        (context.metricLabel
          ? `Explain ${context.metricLabel} for ${context.asOfLabel || 'this period'}: what it is, why it moved, and the next specific data to inspect.`
          : 'Explain this and what I should look at next.')
      const active = startSession('evidence', context, { ...opts, seedPrompt })
      hydratePersistedInstantCard(active, !!opts.instantCard)
    },
    [buildContext, hydratePersistedInstantCard, startSession],
  )

  const openAsk = useCallback(() => {
    // Open-ended "Ask the analyst" is paused until the backend answers well
    // enough — the launchers are hidden, and this guards any stray caller.
    if (!isOpenEndedAskEnabled()) return
    const context = buildContext({})
    startSession('ask', context, {})
  }, [buildContext, startSession])

  const close = useCallback(() => setSession(null), [])

  const openReport = useCallback(
    (period?: string | null) => {
      setReportPeriod(toMonthKey(period || pageRef.current.period || pageRef.current.asOfLabel))
    },
    [],
  )

  const tagToReport = useCallback(
    async (item: Partial<CfoReportAnalysisItem>) => {
      const wb = String(workbookId || '').trim()
      if (!wb) return
      const period = item.period || session?.monthKey || toMonthKey(pageRef.current.period)
      await appApi.appendQuickBooksCfoMonthlyAnalysisReportItem(wb, {
        period,
        item: {
          ...item,
          period,
          title: item.title || 'Tagged analysis',
        },
      })
    },
    [appApi, session, workbookId],
  )

  const value = useMemo<CfoAnalysisContextValue>(
    () => ({ openAnalysis, openAsk, openReport, tagToReport, close, registerPageContext }),
    [openAnalysis, openAsk, openReport, tagToReport, close, registerPageContext],
  )

  const prefetchedEnrichment = useMemo(
    () => selectCachedInstantCardEnrichment(session?.instantCard, enrichmentPackets),
    [enrichmentPackets, session?.instantCard],
  )

  return (
    <Ctx.Provider value={value}>
      {children}
      {session ? (
        <CfoAnalysisModal
          key={session.id}
          appApi={appApi}
          workerClient={workerClient}
          workbookId={workbookId}
          session={session}
          history={history}
          historyQuery={historyQuery}
          onHistoryQueryChange={setHistoryQuery}
          historyArchived={historyArchived}
          onToggleHistoryArchived={setHistoryArchived}
          onClose={close}
          onReopen={reopenSession}
          onContinue={continueSession}
          onPersistSession={persistSession}
          onDeleteSession={deleteSession}
          onArchiveSession={archiveSession}
          onTagToReport={tagToReport}
          onOpenReport={openReport}
          prefetchedEnrichment={prefetchedEnrichment}
          onEnrichmentPacketReady={(packet) => rememberEnrichmentPacket(packet, session.instantCard)}
        />
      ) : null}
      {reportPeriod ? (
        <CfoMonthlyReportPanel
          appApi={appApi}
          workbookId={workbookId}
          period={reportPeriod}
          onClose={() => setReportPeriod(null)}
        />
      ) : null}
    </Ctx.Provider>
  )
}

export default CfoAnalysisProvider
